import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export const CONSENT_STORAGE_KEY = 'drgd_consent';
export const CONSENT_VERSION = 1;
export const CONSENT_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 365; // 12 months

export const DEFAULT_CONSENT = { analytics: true, ads: true, optout: false };

export const readGpc = () => {
  if (typeof navigator === 'undefined') {
    return false;
  }
  return navigator.globalPrivacyControl === true;
};

export const readSavedConsent = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    if (Date.now() - (parsed.ts || 0) > CONSENT_MAX_AGE_MS) {
      return null;
    }
    return { analytics: !!parsed.analytics, ads: !!parsed.ads, optout: !!parsed.optout };
  } catch (_) {
    return null;
  }
};

export const persistConsent = (consent) => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(
      CONSENT_STORAGE_KEY,
      JSON.stringify({ v: CONSENT_VERSION, ts: Date.now(), ...consent })
    );
  } catch (_) {
    // Ignore storage errors (private browsing, quota, etc).
  }
};

// GPC always wins, even over a previously saved "on" choice.
export const resolveConsent = () => {
  const saved = readSavedConsent();
  const base = saved || DEFAULT_CONSENT;
  const consent = { ...base };
  if (readGpc()) {
    consent.ads = false;
    consent.optout = true;
  }
  return consent;
};

export const pushConsentUpdate = (consent) => {
  if (typeof window === 'undefined') {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(['consent', 'update', {
    analytics_storage: consent.analytics ? 'granted' : 'denied',
    ad_storage: consent.ads ? 'granted' : 'denied',
    ad_user_data: consent.ads ? 'granted' : 'denied',
    ad_personalization: consent.ads ? 'granted' : 'denied',
  }]);
};

export const ConsentContext = createContext(null);

export const ConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(DEFAULT_CONSENT);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [gpcActive, setGpcActive] = useState(false);

  useEffect(() => {
    const resolved = resolveConsent();
    setConsent(resolved);
    setGpcActive(readGpc());
    if (typeof window !== 'undefined') {
      window.CGR_CONSENT = resolved;
    }
  }, []);

  const openPanel = useCallback(() => setIsPanelOpen(true), []);
  const closePanel = useCallback(() => setIsPanelOpen(false), []);

  const applyConsent = useCallback((next, { reloadOnRestrict = false } = {}) => {
    const willRestrict = (consent.analytics && !next.analytics) || (consent.ads && !next.ads);
    persistConsent(next);
    setConsent(next);
    pushConsentUpdate(next);
    window.CGR_CONSENT = next;
    setIsPanelOpen(false);
    window.dispatchEvent(new CustomEvent('drgd:consent-changed', { detail: next }));
    if (reloadOnRestrict && willRestrict && typeof window !== 'undefined') {
      window.location.reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consent]);

  const savePreferences = useCallback(
    (next) => applyConsent({ analytics: !!next.analytics, ads: !!next.ads, optout: !!next.optout }, { reloadOnRestrict: true }),
    [applyConsent]
  );

  const acceptAll = useCallback(
    () => applyConsent({ analytics: true, ads: true, optout: false }),
    [applyConsent]
  );

  const rejectAll = useCallback(
    () => applyConsent({ analytics: false, ads: false, optout: true }, { reloadOnRestrict: true }),
    [applyConsent]
  );

  const value = useMemo(
    () => ({ consent, gpcActive, isPanelOpen, openPanel, closePanel, savePreferences, acceptAll, rejectAll }),
    [consent, gpcActive, isPanelOpen, openPanel, closePanel, savePreferences, acceptAll, rejectAll]
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};

export const useConsent = () => {
  const context = useContext(ConsentContext);
  if (!context) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return context;
};
